import { AjaxBasics } from '../../helpers/ajaxBasics'
import { BindAll } from 'lodash-decorators'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiCourse, EnumApiHomework, EnumApiProduct } from '@xt/client/api'
import { EnumBizType, EnumProductType, EnumVisibleRange, MomentSourceType } from '@xt/client/enums'
import { CourseWaterfall } from './waterfall'
import { action, observable } from 'mobx'
import { StatisticsData } from './statistics'
import { GuideData } from './guide'
import { UserContent } from './user-content'
import { Summary } from './summary'
import type { UploadSuccessItem } from '@xt/client/utils/uploader/types.d'
import type { CourseDetail, ProductDetail, CourseMapDetail, WaterfallMoment } from './types.d'

@BindAll()
export class ControllerV2Course {
  constructor($ajax: AjaxBasics) {}
  @observable courseInfo: CourseDetail | null = null
  @observable mapInfo: CourseMapDetail | null = null
  @observable productInfo: ProductDetail | null = null

  // 管理课程页面瀑布流数据
  CourseWaterfall = new CourseWaterfall()

  // 页面学习进度和加群数据
  StatisticsData = new StatisticsData()

  // 作业日记详情弹窗
  UserContent = new UserContent()

  // 引导页数据
  GuideData = new GuideData()

  // 学习小结数据
  Summary = new Summary()

  // 获取作业任务详情数据
  async getHomeworkTaskDetail(taskId: number) {
    const [error, data] = await $service<{
      content: string
      homeworkBizType: number
      homeworkType: number
      id: number
      selectiveSubmit: number
      title: number
    }>({
      url: EnumApiHomework.HomeworkTaskDetail,
      method: 'GET',
      params: {
        id: taskId
      },
      data: {
        id: taskId
      }
    })

    if (error) {
      return null
    }

    return {
      contentRequired: [2, 3].includes(data.homeworkType),
      imageRequired: [1, 3].includes(data.homeworkType),
      homeworkBizType: data.homeworkBizType,
      description: data.content
    }
  }

  async initPageData(courseId: string) {
    if (!courseId) throw new Error('缺少课程id')

    const [err1, course] = await $service<CourseDetail>({
      url: EnumApiCourse.CourseDetails,
      data: { id: courseId }
    })

    if (err1) throw err1

    // 商品数据
    const [err2, product] = await $service<ProductDetail>({
      url: EnumApiProduct.productDetail,
      data: {
        bizId: courseId,
        productType: course.courseType === 1 ? EnumProductType.single : course.courseType === 2 ? EnumProductType.fullstage : ''
      }
    })

    if (err2) throw err2

    // 课程没有购买 将用户推到首页
    if (product.hasOwn === false) {
      return false
    }

    // 获取地图数据
    const [err3, map] = await $service<CourseMapDetail>({
      url: EnumApiCourse.CourseMap,
      data: {
        id: courseId
      }
    })

    if (err3) throw err3

    this.setPageData(course, map, product)
    return true
  }

  // 交作业
  async submitHomework(
    taskId: number,
    data: {
      content: string
      isPublic: boolean
      title: string
      images: Array<Pick<UploadSuccessItem, 'waterUrl' | 'originUrl' | 'originalWidth' | 'originalHeight'>>
    }
  ) {
    const [error, success] = await $service<boolean>({
      method: 'POST',
      url: EnumApiHomework.HomeworkAdd,
      data: {
        bizType: EnumBizType.Course,
        content: data.content.trim(),
        contentNum: data.content.trim().length,
        homeworkTaskId: taskId,
        publicVisible: data.isPublic,
        sun: false,
        title: data.title,
        picList: data.images.map(i => ({
          originWidth: i.originalWidth,
          originHeight: i.originalHeight,
          originUrl: i.originUrl,
          originalWidth: i.originalWidth,
          originalHeight: i.originalHeight,
          originalUrl: i.originUrl,
          waterUrl: i.waterUrl
        }))
      }
    })

    return success === true
  }

  async submitDairyV2(
    guildId: number,
    data: {
      content: string
      isPublic: boolean
      title: string
      images: Array<Pick<UploadSuccessItem, 'waterUrl' | 'originUrl' | 'originalWidth' | 'originalHeight'>>
    }
  ) {
    const [error, result] = await $service<WaterfallMoment>({
      method: 'POST',
      url: EnumApiHomework.MomentAdd,
      data: {
        content: data.content.trim(),
        contentLength: data.content.trim().length,
        title: data.title,
        visibleRange: data.isPublic ? EnumVisibleRange.Public : EnumVisibleRange.Privacy,
        sourceType: MomentSourceType.Guild,
        sourceId: guildId,
        pictures: data.images.map(i => ({
          originWidth: i.originalWidth,
          originHeight: i.originalHeight,
          originUrl: i.originUrl,
          originalWidth: i.originalWidth,
          originalHeight: i.originalHeight,
          originalUrl: i.originUrl,
          waterUrl: i.waterUrl
        }))
      }
    })

    return result
  }

  // 交日记
  async submitDairy(
    courseId: number,
    data: {
      content: string
      isPublic: boolean
      title: string
      images: Array<Pick<UploadSuccessItem, 'waterUrl' | 'originUrl' | 'originalWidth' | 'originalHeight'>>
    }
  ) {
    const [error, result] = await $service<WaterfallMoment>({
      method: 'POST',
      url: EnumApiHomework.MomentAdd,
      data: {
        content: data.content.trim(),
        contentLength: data.content.trim().length,
        title: data.title,
        visibleRange: data.isPublic ? EnumVisibleRange.Public : EnumVisibleRange.Privacy,
        sourceType: 3,
        sourceId: courseId,
        pictures: data.images.map(i => ({
          originWidth: i.originalWidth,
          originHeight: i.originalHeight,
          originUrl: i.originUrl,
          originalWidth: i.originalWidth,
          originalHeight: i.originalHeight,
          originalUrl: i.originUrl,
          waterUrl: i.waterUrl
        }))
      }
    })

    return result
  }

  @action
  setPageData(course: CourseDetail, map: CourseMapDetail, product: ProductDetail) {
    this.courseInfo = course
    this.mapInfo = map
    this.productInfo = product
  }

  // 重置所有状态的方法
  @action
  reset() {
    this.courseInfo = null
    this.mapInfo = null
    this.productInfo = null
    this.StatisticsData.reset()
    this.CourseWaterfall.reset()
    this.Summary.reset()
  }
}
